import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Modal } from '@/components/Modal';
import {
  useWizard,
  useWizardFooterConfig,
  WizardModalFooter,
  WizardProgressBar,
  WizardStepIndicator,
} from '@/components/wizards';
import { useGetSubscription } from '@/hooks/useSubscription';
import { SeedTypeOption } from '@/types';

import type {
  AddSeedByTypeModalProps,
  SeedWizardFormData,
} from './AddSeedByTypeModal.types';
import { useAddSeedSubmit } from './hooks';
import { DataEntryStep, TypeSelectionStep } from './steps';
import {
  DEFAULT_SEED_FORM_DATA,
  seedWizardSteps,
  STEP_IDS,
} from './wizardConfig';

/**
 * AddSeedByTypeModal - A wizard-based modal for adding seeds by type
 *
 * Simplified to only handle text input - file imports are now handled
 * via the ImportSeedsModal flow.
 *
 * ## Architecture Decisions
 * - Wizard handles navigation and form data (seedType, textInput)
 * - File imports delegated to ImportSeedsModal (opened via onOpenImport)
 * - canSubmit validation checks for non-empty text input
 * - Subscription validation intercepts handleNext to block locked features
 */
export const AddSeedByTypeModal = ({
  open,
  onClose,
  onSuccess,
  onOpenImport,
}: AddSeedByTypeModalProps) => {
  // Subscription check for web applications
  const { data: subscription } = useGetSubscription();
  const isWebApplicationsLocked =
    !subscription?.attackSurface?.applications ||
    subscription.numberOfApplications === 0;

  // Subscription error state (displayed on type selection step)
  const [subscriptionError, setSubscriptionError] = useState<string | null>(
    null
  );

  // Wizard hook for navigation and core form data
  const wizard = useWizard<SeedWizardFormData>({
    steps: seedWizardSteps,
    initialData: DEFAULT_SEED_FORM_DATA,
  });

  // Handle closing modal with cleanup
  const handleClose = () => {
    wizard.reset();
    setSubscriptionError(null);
    onClose();
  };

  // Handle Import button click - close this modal and open Import modal
  const handleOpenImport = () => {
    if (onOpenImport) {
      handleClose();
      onOpenImport();
    }
  };

  // Submission hook (simplified - text input only)
  const { handleSubmit, isPending, isError, error, reset } = useAddSeedSubmit({
    formData: wizard.formData,
    onSuccess,
    onClose: handleClose,
  });

  // Single consolidated reset effect on modal open
  // Use setTimeout to defer setState, avoiding synchronous cascading renders
  useEffect(() => {
    if (open) {
      wizard.reset();
      const timer = setTimeout(() => {
        setSubscriptionError(null);
        reset();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Clear text when seed type changes
  useEffect(() => {
    wizard.setFormData(prev => ({ ...prev, textInput: '' }));
  }, [wizard.formData.seedType]);

  // Handle text input changes
  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    wizard.setFormData(prev => ({ ...prev, textInput: e.target.value }));
  };

  // Subscription validation intercept on handleNext
  const handleNextWithSubscriptionCheck = () => {
    const { seedType } = wizard.formData;

    // Check if web applications are locked for this subscription
    if (
      (seedType === SeedTypeOption.WEB_APPLICATION ||
        seedType === SeedTypeOption.WEB_SERVICE) &&
      isWebApplicationsLocked
    ) {
      setSubscriptionError(
        'Please contact your MSP to enable Web testing in your subscription'
      );
      return;
    }

    setSubscriptionError(null);
    wizard.navigation.handleNext();
  };

  // Validation - require non-empty text input
  const canSubmit = useMemo(() => {
    const hasText = wizard.formData.textInput.trim().length > 0;
    return hasText && !isPending;
  }, [wizard.formData.textInput, isPending]);

  // Footer configuration using wizard hook
  const footerConfig = useWizardFooterConfig({
    canGoNext:
      wizard.currentStepId === STEP_IDS.SELECT_TYPE
        ? wizard.navigation.canGoNext
        : canSubmit,
    canGoBack: wizard.navigation.canGoBack,
    isPending,
    isLastStep: wizard.navigation.isLastStep,
    onNext: handleNextWithSubscriptionCheck,
    onBack: wizard.navigation.handleBack,
    onCancel: handleClose,
    onSubmit: handleSubmit,
    labels: { submit: 'Add Seeds', submitting: 'Adding...' },
  });

  // Render current step based on wizard state
  const renderCurrentStep = () => {
    const commonProps = {
      formData: wizard.formData,
      setFormData: wizard.setFormData,
      isSubmitting: isPending,
    };

    switch (wizard.currentStepId) {
      case STEP_IDS.SELECT_TYPE:
        return (
          <TypeSelectionStep
            {...commonProps}
            subscriptionError={subscriptionError}
          />
        );

      case STEP_IDS.ADD_SEEDS:
        return (
          <DataEntryStep
            {...commonProps}
            onTextChange={handleTextChange}
            onOpenImport={handleOpenImport}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={
        <div className="flex w-full items-baseline justify-between">
          <span>Add Seeds</span>
          <WizardStepIndicator
            currentStep={wizard.progress.currentDisplayStep}
            totalSteps={wizard.progress.totalDisplaySteps}
            stepTitle={wizard.progress.currentStepTitle}
          />
        </div>
      }
      size="xl"
      open={open}
      onClose={handleClose}
      hideFooter={true}
    >
      <div className="flex flex-col gap-4">
        {/* Progress Bar */}
        <WizardProgressBar
          currentStep={wizard.progress.currentDisplayStep}
          totalSteps={wizard.progress.totalDisplaySteps}
          stepTitle={wizard.progress.currentStepTitle}
        />

        {/* Current Step Content */}
        {renderCurrentStep()}

        {/* Error Display */}
        {isError && error && (
          <div className="border-chariot-stroke bg-chariot-background-secondary mt-4 rounded-md border p-3 pl-4">
            {error.message.split('\n').length > 0 && (
              <div className="mb-2 space-y-1">
                {error.message
                  .split('\n')
                  .slice(0, 3)
                  .map((errorLine, idx) => (
                    <div
                      key={idx}
                      className="text-chariot-stroke-critical text-xs"
                    >
                      {errorLine}
                    </div>
                  ))}
                {error.message.split('\n').length > 3 && (
                  <div className="text-chariot-text-secondary text-xs">
                    and {error.message.split('\n').length - 3} more error(s)
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Wizard-specific footer with proper button layout */}
      <WizardModalFooter config={footerConfig} />
    </Modal>
  );
};
